import React, { useState } from "react";
import { connect } from "react-redux";
import { Field, FieldArray, formValueSelector } from "redux-form";
import { useCurrentUser } from "APP_ROOT/hooks";
import { toOptions } from "LIB/forms";
import { properCase } from "LIB/formatters/text";
import {
  ADMIN,
  HIGH_CONFIDENCE_BUYER,
  SALES,
  PO_APPROVAL_ROLES,
  QUALITY_CONTROL,
  SUBROLES_BY_ROLE,
  USER_ROLES,
  WAREHOUSE,
  ACCOUNTING,
  ROLE,
  SUBROLE,
  EXCHANGE_COMMISSIONS,
  COMMISSION_CREDIT_PERCENT,
  BUYING_LIMIT,
  REPAIR,
  NOTIFICATION_ROLES,
  NOTIFICATION_ROLES_ARRAY,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  PERMISSIONS,
  PERMISSIONS_OPTIONS,
  ROLES_WITH_PERMISSION,
  SUBROLES_WITH_PERMISSION,
  SALES_MANAGER,
  TEAM_LEADER_CREDIT_PERCENT,
  TEAM_LEADER_COMMISSION_ENABLED,
  AIRLINE_SERVICES_NOTIFICATION
} from "MODULES/users/constants";
import {
  FormInput,
  Select,
  Check,
  CheckGroup
} from "MODULES/shared/components/Forms";
import DashedSeparator from "MODULES/shared/components/DashedSeparator";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { Col, FormGroup } from "react-bootstrap";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import classNames from "classnames";
import NoteTooltip from "MODULES/shared/components/NoteTooltip/NoteTooltip";
import { hasAnyOfRoles } from "MODULES/users/helpers";
import TeamMembers from "MODULES/users/components/Admin/TeamMembers";

export function AdminFields({
  canEdit,
  role,
  getFieldValue,
  setFieldValue,
  user
}) {
  const currentUser = useCurrentUser();
  const [hasCommission, setHasCommission] = useState(
    Number(getFieldValue(COMMISSION_CREDIT_PERCENT)) > 0 ||
      Number(getFieldValue(TEAM_LEADER_CREDIT_PERCENT)) > 0
  );

  const labelClasses = classNames("btn btn-default", {
    active: hasCommission
  });

  const disablePermissions = () => {
    setFieldValue(PERMISSIONS)([]);
    setFieldValue(SUBROLE)(null);
  };

  return (
    canEdit && (
      <div>
        <DashedSeparator />
        <Field
          name={ROLE}
          component={Select}
          label="Role & Permissions"
          options={toOptions(USER_ROLES)}
          onChange={disablePermissions}
          required
        />
        {[QUALITY_CONTROL, WAREHOUSE, ACCOUNTING, SALES].includes(role) && (
          <FormGroup>
            <Field
              name={SUBROLE}
              component={Select}
              label={[
                properCase(SUBROLE),
                getFieldValue(SUBROLE) === SALES_MANAGER && (
                  <NoteTooltip
                    color="rgba(255, 255, 255, 0.25)"
                    icon="fa-info-circle"
                    note="Can set Tier-Types for Inventory.  Can delete Legacy Inventory"
                  />
                )
              ]}
              options={toOptions(SUBROLES_BY_ROLE[role])}
            />
          </FormGroup>
        )}

        <FormRow>
          <Col sm={12}>
            <div className="form-group">
              <div
                className="btn-group btn-group-justified btn-group-vertical"
                data-toggle="buttons"
              >
                <Field
                  name="can_sign_electronically"
                  component={Check}
                  label="QC Auth Rep Digital Signature"
                />
              </div>
            </div>
          </Col>
        </FormRow>

        <FormRow>
          <Col md={12}>
            <FormGroup>
              <label className="control-label" htmlFor={NOTIFICATION_ROLES}>
                {properCase(NOTIFICATION_ROLES)}{" "}
                <NoteTooltip
                  color="rgba(255, 255, 255, 0.25)"
                  icon="fa-info-circle"
                  note="Choose additional roles you'd like this user to receive notifications for."
                />
              </label>
              <div
                id="notification_roles"
                className="btn-group btn-group-vertical btn-group-justified"
                data-toggle="buttons"
              >
                {NOTIFICATION_ROLES_ARRAY.map(f => {
                  const roles = getFieldValue(NOTIFICATION_ROLES) || [];
                  const hasRole = roles.includes(f);
                  const roleHasNotification = getFieldValue(ROLE) === f;
                  const subRoleHasNotification = getFieldValue(SUBROLE) === f;
                  const active = classNames("btn btn-default", {
                    active:
                      hasRole || roleHasNotification || subRoleHasNotification
                  });
                  return (
                    <label
                      className={active}
                      htmlFor={f}
                      onClick={() => {
                        if (hasRole) {
                          const index = roles.indexOf(f);
                          roles.splice(index, 1);
                          setFieldValue(NOTIFICATION_ROLES)(roles);
                        } else {
                          setFieldValue(NOTIFICATION_ROLES)(roles.concat(f));
                        }
                      }}
                    >
                      <i className="fa fa-lg fa-check-circle" />
                      &nbsp;&nbsp;{properCase(f)}
                    </label>
                  );
                })}
              </div>
            </FormGroup>
          </Col>
        </FormRow>

        <FormRow>
          <Col md={12}>
            <FormGroup>
              <label className="control-label" htmlFor={PERMISSIONS}>
                {properCase(PERMISSIONS)}{" "}
                <NoteTooltip
                  color="rgba(255, 255, 255, 0.25)"
                  icon="fa-info-circle"
                  note="Override role permissions."
                />
              </label>
              <div
                id={PERMISSIONS}
                className="btn-group btn-group-vertical btn-group-justified"
                data-toggle="buttons"
              >
                {Object.keys(PERMISSIONS_OPTIONS).map(f => {
                  const permissions = getFieldValue(PERMISSIONS) || [];
                  const hasPermission = permissions.includes(f);
                  const roleHasPermission =
                    ROLES_WITH_PERMISSION[f] &&
                    ROLES_WITH_PERMISSION[f].includes(getFieldValue(ROLE));
                  const subroleHasPermission =
                    SUBROLES_WITH_PERMISSION[f] &&
                    SUBROLES_WITH_PERMISSION[f].includes(
                      getFieldValue(SUBROLE)
                    );

                  const active = classNames("btn btn-default", {
                    active:
                      hasPermission || roleHasPermission || subroleHasPermission
                  });
                  return (
                    <label
                      className={active}
                      htmlFor={f}
                      onClick={() => {
                        if (hasPermission) {
                          const index = permissions.indexOf(f);
                          permissions.splice(index, 1);
                          setFieldValue(PERMISSIONS)(permissions);
                        } else {
                          setFieldValue(PERMISSIONS)(permissions.concat(f));
                        }
                      }}
                    >
                      <i className="fa fa-lg fa-check-circle" />
                      &nbsp;&nbsp;{PERMISSIONS_OPTIONS[f].text}
                    </label>
                  );
                })}
              </div>
            </FormGroup>
          </Col>
        </FormRow>

        {(hasAnyOfRoles(
          {
            role: getFieldValue(ROLE),
            permissions: getFieldValue(PERMISSIONS)
          },
          [ADMIN, REPAIR, SALES]
        ) ||
          WAREHOUSE_LOGISTICS_DIRECTOR === getFieldValue(SUBROLE)) &&
          currentUser.role === ADMIN && (
            <FormRow>
              <Col md={hasCommission ? 6 : 12}>
                <FormGroup>
                  <div
                    id="has_commission"
                    className="btn-group btn-group-justified"
                    data-toggle="buttons"
                    style={{ marginTop: 21 }}
                  >
                    <label
                      className={labelClasses}
                      htmlFor="has_commissions"
                      onClick={() => {
                        if (hasCommission) {
                          setFieldValue(COMMISSION_CREDIT_PERCENT)(0);
                        }
                        setHasCommission(!hasCommission);
                      }}
                    >
                      <i className="fa fa-lg fa-check-circle" />
                      &nbsp;&nbsp;Enable Commissions
                    </label>
                  </div>
                </FormGroup>
              </Col>
              {hasCommission && (
                <Col md={6}>
                  <Field
                    name={COMMISSION_CREDIT_PERCENT}
                    component={FormInput}
                    type={"number"}
                    label="Commission Percent"
                  />
                </Col>
              )}
            </FormRow>
          )}
        {hasCommission && [
          <FormRow>
            <Col md={6}>
              <CheckGroup title={properCase(TEAM_LEADER_COMMISSION_ENABLED)}>
                <Field
                  name={TEAM_LEADER_COMMISSION_ENABLED}
                  component={Check}
                  label="Team Leader Commission"
                />
              </CheckGroup>
            </Col>
          </FormRow>,
          getFieldValue(TEAM_LEADER_COMMISSION_ENABLED) && (
            <FieldArray
              name="team_members"
              component={TeamMembers}
              teamMembers={user.team_members}
            />
          )
        ]}
        <FormRow>
          <Col md={6}>
            <Field
              name="po_approval_role"
              component={Select}
              label="PO Approval Permissions"
              options={toOptions(PO_APPROVAL_ROLES)}
              prompt={false}
              required
            />
          </Col>
          <Col md={6}>
            <CheckGroup title={properCase(AIRLINE_SERVICES_NOTIFICATION)}>
              <Field
                name={AIRLINE_SERVICES_NOTIFICATION}
                component={Check}
                label={[
                  properCase(AIRLINE_SERVICES_NOTIFICATION),
                  <NoteTooltip
                    color="rgba(255, 255, 255, 0.25)"
                    icon="fa-info-circle"
                    note={
                      "Sends Notification when an OQ meet these criteria:\n\n1. Customer Type = Airline; and\n\n2. OQ = Offer; and\n\n3. Offer Type=LookingFor; and 4. Company has Sellable Stock"
                    }
                  />
                ]}
              />
            </CheckGroup>
          </Col>
        </FormRow>
        <div className="form-group">
          <div
            className="btn-group btn-group-justified btn-group-vertical"
            data-toggle="buttons"
          >
            <Field
              name={HIGH_CONFIDENCE_BUYER}
              component={Check}
              label={[
                properCase(HIGH_CONFIDENCE_BUYER),
                <NoteTooltip
                  note="No Tier Type Restrictions"
                  icon="fa-info-circle"
                  color="rgba(255, 255, 255, 0.25)"
                />
              ]}
            />
            {role === ACCOUNTING && (
              <Field
                name={EXCHANGE_COMMISSIONS}
                component={Check}
                label={[
                  "Receives Exchange Commission",
                  <NoteTooltip
                    note="For 2nd and 3rd exchanges only"
                    icon="fa-info-circle"
                    color="rgba(255, 255, 255, 0.25)"
                  />
                ]}
              />
            )}

            <Field
              name={BUYING_LIMIT}
              component={FormInput}
              label={properCase(BUYING_LIMIT)}
              type="number"
              inputProps={{ min: "0" }}
            />
          </div>
        </div>
      </div>
    )
  );
}

export default connect((state, ownProps) => ({
  role: formValueSelector(ownProps.formName)(state, ROLE)
}))(withFieldAccessors(AdminFields));



// WEBPACK FOOTER //
// ./app/modules/users/components/Admin/Fields.jsx